import { memo } from "react";
import { Table as AntTable } from "antd";
import type { TableProps as AntTableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CustomPagination } from "src/components/ui/Pagination";
import Loader from "src/components/layout/Loader";
import { cn } from "src/util/cn";
import "./CustomTable.css";

export interface CustomTableProps<T> extends Omit<AntTableProps<T>, "pagination" | "loading"> {
  data: T[];
  columns: ColumnsType<T>;
  pageSize: number;
  currentPage: number;
  totalCount: number;
  handlePagination: (page: number) => void;
  handlePageSizeChange?: (size: number) => void;
  isLoading?: boolean;
  rowSelection?: AntTableProps<T>["rowSelection"];
  rowKey?: string | ((record: T) => string);
  onChange?: AntTableProps<T>["onChange"];
  className?: string;
}

function CustomTableInner<T extends object>({
  data,
  columns,
  pageSize,
  currentPage,
  totalCount,
  handlePagination,
  handlePageSizeChange,
  isLoading = false,
  rowSelection,
  rowKey,
  onChange,
  className,
  ...rest
}: CustomTableProps<T>) {
  if (isLoading) {
    return <Loader height="h-64" />;
  }

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <AntTable<T>
        dataSource={data}
        columns={columns}
        rowKey={rowKey as string}
        rowSelection={rowSelection}
        onChange={onChange}
        pagination={false}
        className="custom-table"
        {...rest}
      />
      <CustomPagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        setCurrentPage={handlePagination}
        setPageSize={handlePageSizeChange}
        showRowsPerPage={!!handlePageSizeChange}
      />
    </div>
  );
}

const CustomTable = memo(CustomTableInner) as typeof CustomTableInner;

export default CustomTable;
